/**
 * Resolves phone numbers to member name/email (via the Communications
 * /api/members/resolve-phones endpoint → MemberManagement). Batches + caches so a
 * view can resolve all visible numbers in one call, then look each up synchronously.
 * Keys are the local significant digits, so any stored format (0xx / +27 / 27 / bare)
 * resolves to the same contact.
 */

import { useCallback, useRef, useState } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';

export interface MemberContact {
  phone: string;
  name?: string | null;
  email?: string | null;
  isMember: boolean;
}

/** Strip a leading +27/27 country code or 0 trunk prefix → local significant digits. */
export function localDigits(s?: string | null): string {
  let d = (s || '').replace(/\D/g, '');
  if (d.startsWith('27') && d.length > 2) d = d.slice(2);
  else if (d.startsWith('0') && d.length > 1) d = d.slice(1);
  return d;
}

export function useMemberLookup() {
  const httpClient = useHttpClient();
  const [map, setMap] = useState<Record<string, MemberContact>>({});
  // undefined = not yet fetched; null = fetched, no match; object = matched.
  const cache = useRef<Record<string, MemberContact | null>>({});

  const resolve = useCallback(async (phones: (string | null | undefined)[]) => {
    const toFetch = Array.from(new Set(
      phones
        .map((p) => ({ raw: String(p ?? '').trim(), key: localDigits(p) }))
        .filter((x) => x.raw && x.key.length >= 6 && cache.current[x.key] === undefined)
        .map((x) => x.raw),
    ));
    if (toFetch.length === 0) return;

    // Mark requested keys as "fetched, no match" up front to avoid duplicate requests.
    toFetch.forEach((p) => { cache.current[localDigits(p)] = null; });

    try {
      const resp = await httpClient.post<MemberContact[]>(getApiUrl('communications', '/api/members/resolve-phones'), { phones: toFetch });
      const results = (resp.data as MemberContact[]) ?? [];
      if (results.length === 0) return;
      results.forEach((r) => { cache.current[localDigits(r.phone)] = r; });
      setMap((prev) => {
        const next = { ...prev };
        results.forEach((r) => { next[localDigits(r.phone)] = r; });
        return next;
      });
    } catch {
      /* fail soft — rows just show without member labels */
    }
  }, [httpClient]);

  const lookup = useCallback((phone?: string | null): MemberContact | undefined => {
    const key = localDigits(phone);
    return key ? map[key] : undefined;
  }, [map]);

  return { resolve, lookup };
}
